"use client"

import { keepPreviousData, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { httpLink, unstable_httpBatchStreamLink } from "@trpc/client"
import { createTRPCReact, getQueryKey } from "@trpc/react-query"
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server"
import { PropsWithChildren, useState } from "react"
import superjson from "superjson"

import type { AppRouter } from "@/server/api/root"
import { getBaseUrl } from "@/utils/next"

const MINUTE = 60 * 1000

function createQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10 * MINUTE,
        gcTime: 30 * MINUTE,
        refetchOnWindowFocus: false,
      },
    },
  })

  // TODO refaktorovat vsetky query do samostatnych hookov a nastavit samostatne
  const listKeys = [
    getQueryKey(api.session.listSessions),
    getQueryKey(api.session.listSessionsCount),
    getQueryKey(api.session.listClaims),
    getQueryKey(api.session.listClaimsCount),
  ]

  // Lists are refreshed more often
  for (const listKey of listKeys) {
    queryClient.setQueryDefaults(listKey, { staleTime: 1 * MINUTE, placeholderData: keepPreviousData })
  }

  return queryClient
}

let clientQueryClientSingleton: QueryClient | undefined

function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient()
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= createQueryClient())
}

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCReact<AppRouter>()

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>

type Props = {
  trpcStreamingDisabled?: boolean
}

export function TrpcProvider(props: PropsWithChildren<Props>) {
  const queryClient = getQueryClient()

  const [trpcClient] = useState(() => {
    const opts = {
      transformer: superjson,
      url: `${getBaseUrl()}/api/trpc`,
      headers: () => {
        const headers = new Headers()
        headers.set("x-trpc-source", "nextjs-react")
        return headers
      },
    }

    return api.createClient({
      links: [props.trpcStreamingDisabled ? httpLink(opts) : unstable_httpBatchStreamLink(opts)],
    })
  })

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{props.children}</QueryClientProvider>
    </api.Provider>
  )
}
